import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { formatPercent } from '../../../shared/numberFormat';

function TaxHandlingTypeForm({ initial, onSave, onCancel, loading }) {
  const [code, setCode] = useState(initial?.code || '');
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [taxCodeId, setTaxCodeId] = useState(initial?.tax_code_id ?? '');
  const [pricingBasis, setPricingBasis] = useState(initial?.pricing_basis || 'business');
  const [retailRounding, setRetailRounding] = useState(String(initial?.retail_rounding_cents ?? 1));
  const [error, setError] = useState('');
  const [taxCodes, setTaxCodes] = useState([]);

  useEffect(() => {
    axios.get('/api/tax-codes', { params: { status: 'active' } })
      .then((res) => setTaxCodes(res.data.data || []))
      .catch(() => setTaxCodes([]));
  }, []);

  const locked = !!initial?.is_system_default;

  // Same active-plus-currently-assigned pattern as
  // CustomerTierForm.js's Tax Handling Type dropdown.
  const taxCodeOptions = taxCodes.filter(
    (tc) => tc.is_active || String(tc.id) === String(taxCodeId)
  );
  const selectedTaxCode = initial?.tax_code;
  if (selectedTaxCode && !taxCodeOptions.some((tc) => String(tc.id) === String(selectedTaxCode.id))) {
    taxCodeOptions.push(selectedTaxCode);
  }

  const validate = () => {
    if (!code.trim()) return 'Code is required.';
    if (!name.trim()) return 'Name is required.';
    if (!taxCodeId) return 'Tax Code is required.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      code: code.trim(),
      name: name.trim(),
      description: description.trim(),
      tax_code_id: Number(taxCodeId),
      pricing_basis: pricingBasis,
      retail_rounding_cents: Number(retailRounding),
    });
  };

  return (
    <form className="tax-handling-type-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Code<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={10}
            placeholder="e.g., BUSINESS"
            disabled={locked}
            style={{ width: 130 }}
          />
        </div>
        <div>
          <label>Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Business (ex GST)"
            style={{ width: 220 }}
          />
        </div>
        <div>
          <label>Tax Code<RequiredMark /></label><br />
          <select
            className="styled-input"
            value={taxCodeId}
            onChange={(e) => setTaxCodeId(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="">Select...</option>
            {taxCodeOptions.map((tc) => (
              <option key={tc.id} value={tc.id}>{tc.code} ({formatPercent(tc.rate)}){!tc.is_active ? ' (inactive)' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      {locked && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          This is a locked Tax Handling Type — its code can't be changed.
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <label>Description</label><br />
        <textarea
          className="styled-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
          placeholder="Optional notes on when this Tax Handling Type is used"
        />
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Pricing Basis<RequiredMark /></label><br />
          <select
            className="styled-input"
            value={pricingBasis}
            onChange={(e) => setPricingBasis(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="business">Business — prices ex GST</option>
            <option value="retail">Retail — prices inc GST</option>
          </select>
        </div>
        {pricingBasis === 'retail' && (
          <div>
            <label>Retail Rounding<RequiredMark /></label><br />
            <select
              className="styled-input"
              value={retailRounding}
              onChange={(e) => setRetailRounding(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <option value="1">Nearest 1c</option>
              <option value="5">Nearest 5c</option>
              <option value="10">Nearest 10c</option>
            </select>
          </div>
        )}
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default TaxHandlingTypeForm;
